import {
  ENROLLMENT_REVIEW_MODE,
  EnrollmentReviewMode,
  fetchCommunicationIdCorrespondingData,
  fetchEnrollmentWithChildCases,
} from '@app-store'
import { QUEUE_STRINGS } from './queueIntake.types'
import { NAMESPACES } from './constants'
import { FaxDetailsData } from '../types/intakeQueue.types'
import { toTitleCase } from './helperFunctions'

export const DATA_REVIEW_STATUSES = [
  NAMESPACES.NEEDS_REVIEW_LOWERCASE,
  NAMESPACES.NEEDS_MASTER_DATA_REVIEW_LOWERCASE,
  NAMESPACES.CASE_DEDUP_REVIEW_LOWERCASE,
] as const

export const INTAKE_STATUS_SNAPSHOT_STORAGE_PREFIX = 'intakeStatusSnapshot:'

export interface IntakeStatusSnapshot {
  initialStatus?: string
}

export const normalizeIntakeStatus = (status?: string) =>
  status?.toLowerCase()?.replace(/[_-]/g, '') ?? ''

export const getIntakeStatusValue = (
  faxDetails?: FaxDetailsData | null
): string | undefined =>
  faxDetails?.enrollment_status?.value ??
  faxDetails?.category_status?.label ??
  undefined

export const hasIntakeStatusChanged = (
  initialStatus?: string,
  currentStatus?: string
) => {
  if (!initialStatus || !currentStatus) return false
  return (
    normalizeIntakeStatus(initialStatus) !==
    normalizeIntakeStatus(currentStatus)
  )
}

export const formatIntakeStatusForDisplay = (status?: string) => {
  if (!status) return ''
  return status
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export const getStatusUpdateNotificationParts = (latestStatus?: string) => {
  const displayStatus =
    toTitleCase(formatIntakeStatusForDisplay(latestStatus)) || 'the updated'
  const [before = '', after = ''] =
    NAMESPACES.STATUS_UPDATE_NOTIFICATION_MESSAGE.split('{status}')
  return { before, status: displayStatus, after }
}

const getIntakeStatusSnapshotStorageKey = (communicationId?: string) =>
  `${INTAKE_STATUS_SNAPSHOT_STORAGE_PREFIX}${communicationId ?? ''}`

export const readIntakeStatusSnapshot = (
  communicationId?: string
): IntakeStatusSnapshot | null => {
  if (!communicationId) return null
  try {
    const raw = sessionStorage.getItem(
      getIntakeStatusSnapshotStorageKey(communicationId)
    )
    return raw ? (JSON.parse(raw) as IntakeStatusSnapshot) : null
  } catch {
    return null
  }
}

export const writeIntakeStatusSnapshot = (
  communicationId: string | undefined,
  state: IntakeStatusSnapshot
) => {
  if (!communicationId) return
  sessionStorage.setItem(
    getIntakeStatusSnapshotStorageKey(communicationId),
    JSON.stringify(state)
  )
}

export const clearIntakeStatusSnapshot = (communicationId?: string) => {
  if (!communicationId) return
  sessionStorage.removeItem(getIntakeStatusSnapshotStorageKey(communicationId))
}

/** Captures initial Enrollment status for a communication (reload-proof). */
export const captureInitialIntakeStatus = (
  communicationId: string | undefined,
  statusOrDetails?: string | FaxDetailsData | null,
  options?: { overwrite?: boolean }
) => {
  if (!communicationId) return
  if (!options?.overwrite) {
    const stored = readIntakeStatusSnapshot(communicationId)
    if (stored?.initialStatus) return
  }

  const initialStatus =
    typeof statusOrDetails === 'string'
      ? statusOrDetails
      : getIntakeStatusValue(statusOrDetails)
  if (!initialStatus) return

  writeIntakeStatusSnapshot(communicationId, {
    initialStatus,
  })
}

export const getEnrollmentStatusFromQueueRow = (
  row?: {
    id?: string | number
    enrollment_status?: { value?: string; label?: string } | string | null
    'enrollment_status.value'?: unknown
    'enrollment_status.label'?: unknown
  } | null
): string | undefined => {
  if (!row) return undefined

  if (typeof row.enrollment_status === 'object' && row.enrollment_status) {
    return row.enrollment_status.label ?? undefined
  }

  return (
    row['enrollment_status.value']?.toString() ??
    row['enrollment_status.label']?.toString() ??
    (typeof row.enrollment_status === 'string'
      ? row.enrollment_status
      : undefined)
  )
}

export const isWebEnrollmentIntake = (
  intakeEntityName?: string,
  faxDetails?: FaxDetailsData | null
) =>
  intakeEntityName === QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE ||
  !!faxDetails?.enrollment_status

/** Fetches live Enrollment or Fax status for cancel / status-update checks. */
export const fetchLatestIntakeStatus = async (
  communicationId: string | undefined,
  isWebEnrollment: boolean
): Promise<string | undefined> => {
  if (!communicationId) return undefined
  const id = Number(communicationId)
  if (Number.isNaN(id)) return undefined

  if (isWebEnrollment) {
    const response = await fetchEnrollmentWithChildCases(id)
    return response?.data?.enrollment_status?.label
  }

  const response = await fetchCommunicationIdCorrespondingData(id, {
    entity_name: QUEUE_STRINGS.COMMUNICATION_LOWERCASE,
    select: ['category_status'],
  })
  return response?.data?.category_status?.label
}

export const resolveReviewMode = (
  entityName?: string,
  enrollmentStatus?: string
): EnrollmentReviewMode => {
  if (entityName !== QUEUE_STRINGS.HCP_PORTAL_ENTITY_LOWERCASE) {
    return ENROLLMENT_REVIEW_MODE.LEGACY
  }

  const status = enrollmentStatus?.toLowerCase()

  if (
    status &&
    DATA_REVIEW_STATUSES.includes(
      status as (typeof DATA_REVIEW_STATUSES)[number]
    )
  ) {
    return ENROLLMENT_REVIEW_MODE.DATA_REVIEW
  }

  if (status === NAMESPACES.PENDING_INTAKE_LOWERCASE) {
    return ENROLLMENT_REVIEW_MODE.CASE_TABS
  }

  return ENROLLMENT_REVIEW_MODE.LEGACY
}

export const getDataReviewLayoutId = (globalVariables?: {
  data_review_layout?: { layout_id?: string }
  data_entry_layout?: { layout_id?: string }
}): string | undefined =>
  globalVariables?.data_review_layout?.layout_id ??
  globalVariables?.data_entry_layout?.layout_id

export interface DataReviewLayoutConfig {
  layoutId?: string
  entityId?: string
  entityName?: string
}
